import React, { useRef, useState, useEffect } from "react";
import axiosPrivate from "../../axios/axiosPrivate";
import useLoader from "../../hooks/useLoader";
import useAuth from "../../hooks/useAuth";

const FloatingInput = ({
  label,
  required = false,
  type = "text",
  disabled = false,
  value,
  onChange,
  onClick,
  inputRef,
  name,
}) => {
  return (
    <div className="relative">
      <input
        ref={inputRef}
        name={name}
        type={type}
        placeholder=" "
        disabled={disabled}
        value={value}
        onChange={onChange}
        onClick={onClick}
        className={`
          shadow-sm
          peer w-full rounded-md border
          px-4 pt-5 pb-3
          text-gray-800
          bg-white
          border-gray-300
          focus:border-blue-400 focus:outline-none
        `}
        style={{
          backgroundColor: disabled ? "#D1D5DB60" : "",
          cursor: disabled ? "not-allowed" : "pointer",
          color: disabled ? "#6B7280" : "",
        }}
      />

      <label
        className={`
          absolute left-4
          px-1
          ${disabled ? "" : "bg-white"}
          pointer-events-none
          transition-all
          text-gray-500 text-base
          top-4
          peer-placeholder-shown:top-4
          peer-placeholder-shown:text-base
          peer-focus:-top-2
          peer-focus:text-sm
          peer-focus:text-blue-500
          peer-[&:not(:placeholder-shown)]:-top-2
          peer-[&:not(:placeholder-shown)]:text-sm
          peer-[&:not(:placeholder-shown)]:text-gray-600
        `}
      >
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
    </div>
  );
};

const EditAddressForm = ({ address, onClose, showCancel, cancelEdit }) => {
  const pincodeRef = useRef(null);
  const firstNameRef = useRef(null);
  const { setLoading } = useLoader();
  const { user, setUser } = useAuth();

  // 🔥 Prefill form with selected address
  const [form, setForm] = useState({
    id: address?.id,
    pincode: address?.zip || "",
    first_name: address?.first_name || "",
    last_name: address?.last_name || "",
    address1: address?.address1 || "",
    address2: address?.address2 || "",
    city: address?.city || "",
    state: address?.province || "",
    email: user?.email || "",
  });

  const [errors, setErrors] = useState({});
  const [pinVerified, setPinVerified] = useState(true);
  const [makeDefault, setMakeDefault] = useState(address?.default || false);

  // 🔥 Handle Pincode Change
  const handlePincodeChange = async (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length > 6) return;

    const isPinChanged = value !== address?.zip;

    setForm((prev) => ({
      ...prev,
      pincode: value,

      // 🔥 CLEAR address lines if pincode changed
      ...(isPinChanged && {
        address1: "",
        address2: "",
      }),
    }));

    setErrors((prev) => ({ ...prev, pincode: "" }));
    setPinVerified(false);

    if (value.length === 6) {
      try {
        setLoading(true);

        const response = await axiosPrivate.get(
          `/address/get-pincode/${value}`
        );

        const data = response?.data?.data?.details;

        setForm((prev) => ({
          ...prev,
          city: data.city,
          state: data.state,
        }));

        setPinVerified(true);

        setTimeout(() => {
          firstNameRef.current?.focus();
        }, 100);
      } catch (err) {
        setErrors((prev) => ({
          ...prev,
          pincode: "Invalid pincode",
        }));
        setPinVerified(false);
      } finally {
        setLoading(false);
      }
    }
  };

  // 🔥 Handle Normal Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (value.length > 250) return;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // 🔥 Submit Updated Address
  const handleSubmit = async () => {
    let newErrors = {};

    if (form.pincode.length !== 6)
      newErrors.pincode = "Pincode must be 6 digits";

    if (!form.first_name.trim())
      newErrors.first_name = "First name is required";

    if (!form.last_name.trim()) newErrors.last_name = "Last name is required";

    if (form.address1.trim().length < 10)
      newErrors.address1 = "Address line 1 must be at least 10 characters";

    if (form.address2.trim().length < 10)
      newErrors.address2 = "Address line 2 must be at least 10 characters";

    if (form.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      if (!emailRegex.test(form.email.trim()))
        newErrors.email = "Enter a valid email address";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setLoading(true);

      const response = await axiosPrivate.put(
        `/address/edit-address/${form.id}`,
        {
          ...form,
          phone: user?.phone,
          makeDefault,
          updateEmail: !user?.email && form.email ? true : false,
        }
      );

      const { updatedAddress, updatedCustomer } = response.data.data;

      // 🔥 Update addresses in context
      let updatedAddresses = user.addresses.map((addr) => {
        if (addr.id === updatedAddress.id) {
          return {
            ...updatedAddress,
            default: makeDefault,
          };
        }

        if (makeDefault) {
          return { ...addr, default: false };
        }

        return addr;
      });

      // 🔥 Move default address to first position
      if (makeDefault) {
        updatedAddresses = [
          updatedAddresses.find((a) => a.id === updatedAddress.id),
          ...updatedAddresses.filter((a) => a.id !== updatedAddress.id),
        ];
      }

      setUser((prev) => ({
        ...prev,
        addresses: updatedAddresses,
        first_name: updatedCustomer?.first_name || prev.first_name,
        last_name: updatedCustomer?.last_name || prev.last_name,
        email: updatedCustomer?.email || prev.email,
      }));

      cancelEdit(); // 🔥 Close bottom sheet
    } catch (error) {
      console.log(error?.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    pincodeRef.current?.focus();
  }, []);

  return (
    <div className="order-address-inner bg-white p-4 rounded-lg space-y-4">
      {/* Pincode */}
      <div>
        <FloatingInput
          label="Pincode"
          required
          value={form.pincode}
          onChange={handlePincodeChange}
          inputRef={pincodeRef}
          name="pincode"
        />
        {errors.pincode && (
          <p className="text-red-500 text-[12px] mt-2">{errors.pincode}</p>
        )}
      </div>

      {/* Names */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FloatingInput
            label="First name"
            required
            disabled={!pinVerified}
            value={form.first_name}
            onChange={handleChange}
            inputRef={firstNameRef}
            name="first_name"
            className="overflow-hidden max-w-[100%] w-[100%]"
          />
          {errors.first_name && (
            <p className="text-red-500 text-[12px] general-sans-medium mt-2">
              {errors.first_name}
            </p>
          )}
        </div>
        <div>
          <FloatingInput
            label="Last name"
            required
            disabled={!pinVerified}
            value={form.last_name}
            onChange={handleChange}
            name="last_name"
            className="overflow-hidden max-w-[100%] w-[100%]"
          />
          {errors.last_name && (
            <p className="text-red-500 text-[12px] general-sans-medium mt-2">
              {errors.last_name}
            </p>
          )}
        </div>
      </div>

      {/* Address Lines */}
      <div>
        <FloatingInput
          label="Flat, house number, floor"
          required
          disabled={!pinVerified}
          value={form.address1}
          onChange={handleChange}
          name="address1"
          className="overflow-hidden max-w-[100%] w-[100%]"
        />
        {errors.address1 && (
          <p className="text-red-500 text-[12px] general-sans-medium mt-2">
            {errors.address1}
          </p>
        )}
      </div>

      <div>
        <FloatingInput
          label="Area, street, sector"
          required
          disabled={!pinVerified}
          value={form.address2}
          onChange={handleChange}
          name="address2"
          className="overflow-hidden max-w-[100%] w-[100%]"
        />
        {errors.address2 && (
          <p className="text-red-500 text-[12px] general-sans-medium mt-2">
            {errors.address2}
          </p>
        )}
      </div>

      {/* City & State */}
      <div className="grid grid-cols-2 gap-3">
        <FloatingInput
          label="City"
          required
          disabled
          value={form.city}
          className="overflow-hidden max-w-[100%] w-[100%]"
        />
        <FloatingInput
          label="State"
          required
          disabled
          value={form.state}
          className="overflow-hidden max-w-[100%] w-[100%]"
        />
      </div>

      {/* Email */}
      <div>
        <FloatingInput
          label="E-mail (optional)"
          type="email"
          disabled={!pinVerified || !!user?.email}
          value={form.email}
          onChange={handleChange}
          name="email"
          className="overflow-hidden max-w-[100%] w-[100%]"
        />
        {errors.email && (
          <p className="text-red-500 text-[12px] general-sans-medium mt-2">
            {errors.email}
          </p>
        )}
      </div>

      {/* Make Default */}
      <div className="flex items-center gap-2 mt-2">
        <input
          type="checkbox"
          checked={makeDefault}
          onChange={(e) => setMakeDefault(e.target.checked)}
        />
        <span className="text-sm">Make this default address</span>
      </div>

      {/* Submit */}
      <div className="flex gap-4 items-center" style={{ marginTop: "26px" }}>
        {showCancel && (
          <button
            className="w-full bg-black text-white p-4 rounded-lg"
            onClick={cancelEdit}
          >
            Cancel
          </button>
        )}
        <button
          className="w-full bg-black text-white p-4 rounded-lg"
          onClick={handleSubmit}
        >
          Update
        </button>
      </div>
    </div>
  );
};

export default EditAddressForm;
